
import React, { useState } from "react";
import LoadingSpinner from "./LoadingSpinner";
import ErrorDisplay from "./ErrorDisplay";
import EmptyProjectsState from "./EmptyProjectsState";
import { useSampleProject } from "@/hooks/useSampleProject";
import { useAuth } from "@/context/auth";

interface InitialLoaderProps {
  initialLoadComplete: boolean;
  isLoading: boolean;
  fetchError: string | null;
  hasProjects: boolean | null;
  selectedFolderId: string | null;
  onRetry: () => void;
  onCreateProject: (name: string) => Promise<void>;
  onProjectCreated?: (projectId: string) => void;
  onProjectSelect?: (projectId: string) => void;
  onFolderSelect?: (folderId: string) => void;
}

const InitialLoader: React.FC<InitialLoaderProps> = ({
  initialLoadComplete,
  isLoading,
  fetchError,
  hasProjects,
  selectedFolderId,
  onRetry,
  onCreateProject,
  onProjectCreated,
  onProjectSelect,
  onFolderSelect
}) => {
  const { user } = useAuth();
  const { createSampleProject, isCreating, currentStep } = useSampleProject();

  const handleCreateSampleProject = async () => {
    if (!user) return;
    
    const projectId = await createSampleProject(user.id, (project) => {
      // Optimistisk uppdatering - visa projektet direkt
      if (onProjectCreated) {
        onProjectCreated(project.id);
      }
    });
  };

  // Show loading only when actually loading and no error
  if (isLoading && !fetchError && !initialLoadComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <main className="flex-1 p-4 sm:p-6 max-w-7xl mx-auto w-full">
          <LoadingSpinner 
            onRetry={onRetry}
            message={currentStep || "Laddar dina projekt..."}
          />
        </main>
      </div>
    );
  }

  // Show error state only after initial load is complete
  if (fetchError && initialLoadComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <main className="flex-1 p-4 sm:p-6 max-w-7xl mx-auto w-full">
          <ErrorDisplay 
            errorMessage={fetchError} 
            onRetry={onRetry} 
          />
        </main>
      </div>
    );
  }

  // Show empty projects state only when we're certain there are no projects and not in a shared folder
  if (hasProjects === false && !selectedFolderId && initialLoadComplete) {
    return (
      <EmptyProjectsState 
        onCreateProject={onCreateProject}
        onCreateSampleProject={handleCreateSampleProject}
        onProjectSelect={onProjectSelect}
        onFolderSelect={onFolderSelect}
      />
    );
  }

  return null;
};

export default InitialLoader;
